import { useState } from "react";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";  // CardHeader, CardTitleを削除
import { Badge } from "./ui/badge";
// Progressを削除
import { 
  GraduationCap,
  Play,
  Pause,
  CheckCircle,
  XCircle,
  Clock,
  Star,
  Award,
  Users,
  BookOpen,
  Target,
  TrendingUp,
  RotateCcw,
  HelpCircle,
  Lightbulb,
  AlertCircle,
  Volume2,
  VolumeX
} from "lucide-react";

export default function TrainingEducationMode() {
  const [activeModule, setActiveModule] = useState("overview");
  const [currentLesson, setCurrentLesson] = useState(null);
  const [userProgress, setUserProgress] = useState({});
  const [quizMode, setQuizMode] = useState(false);
  const [currentQuizQuestion, setCurrentQuizQuestion] = useState(0);
  const [userAnswers, setUserAnswers] = useState([]);
  const [voiceEnabled, setVoiceEnabled] = useState(true);

  // プログレスバーコンポーネントの実装
  const Progress = ({ value, className = "" }) => (
    <div className={`w-full bg-gray-200 rounded-full overflow-hidden ${className}`}>
      <div 
        className="bg-blue-600 h-full transition-all duration-300"
        style={{ width: `${Math.min(Math.max(value, 0), 100)}%` }}
      ></div>
    </div>
  );

  // サンプル学習データ
  const learningModules = [
    {
      id: "basic_scrub",
      title: "基本的な器械出し",
      description: "器械出しの基本技術と手順",
      level: "初級",
      duration: "45分",
      progress: 85,
      lessons: 8,
      completedLessons: 7,
      icon: "🧤",
      color: "bg-blue-500"
    },
    {
      id: "circulator_basics",
      title: "外回り業務の基礎",
      description: "外回り看護師の基本業務",
      level: "初級",
      duration: "60分",
      progress: 60,
      lessons: 10,
      completedLessons: 6,
      icon: "🔄",
      color: "bg-green-500"
    },
    {
      id: "sterilization",
      title: "滅菌・消毒の原則",
      description: "感染管理と滅菌技術",
      level: "中級",
      duration: "90分",
      progress: 30,
      lessons: 12,
      completedLessons: 4,
      icon: "🧽",
      color: "bg-purple-500"
    },
    {
      id: "emergency_procedures",
      title: "緊急時対応",
      description: "手術中の緊急事態への対処",
      level: "上級",
      duration: "120分",
      progress: 10,
      lessons: 15,
      completedLessons: 2,
      icon: "🚨",
      color: "bg-red-500"
    },
    {
      id: "laparoscopic_advanced",
      title: "腹腔鏡手術の高度技術",
      description: "腹腔鏡手術の専門技術",
      level: "専門",
      duration: "180分",
      progress: 0,
      lessons: 20,
      completedLessons: 0,
      icon: "🔬",
      color: "bg-indigo-500"
    }
  ];

  const detailedLessons = [
    {
      id: "lesson_1",
      moduleId: "basic_scrub",
      title: "手術前の準備",
      description: "手洗い、ガウニング、グローブ装着",
      duration: "8分",
      type: "video",
      keyPoints: [
        "正しい手洗い方法（6分間）",
        "ガウンの無菌的着用",
        "グローブの装着技術",
        "無菌野の維持"
      ],
      commonMistakes: [
        "手洗い時間が不十分",
        "グローブ装着時のコンタミ",
        "ガウンの後ろが開く"
      ],
      completed: true,
      score: 95
    },
    {
      id: "lesson_2",
      moduleId: "basic_scrub",
      title: "器械台の準備",
      description: "器械台の設営と器械の配置",
      duration: "12分",
      type: "interactive",
      keyPoints: [
        "器械台の高さ調整",
        "器械の効率的配置",
        "カウント確認",
        "追加器械の準備"
      ],
      commonMistakes: [
        "器械の配置が非効率",
        "カウント漏れ",
        "台の高さが不適切"
      ],
      completed: true,
      score: 88
    },
    {
      id: "lesson_3",
      moduleId: "basic_scrub",
      title: "器械の受け渡し技術",
      description: "術者への器械受け渡しの基本",
      duration: "15分",
      type: "simulation",
      keyPoints: [
        "適切なタイミング",
        "器械の向きと持ち方",
        "術者の視線を妨げない位置",
        "予測的な準備"
      ],
      commonMistakes: [
        "器械の向きが逆",
        "タイミングが遅い",
        "術野を遮る位置"
      ],
      completed: false,
      score: null
    }
  ];

  const quizQuestions = [
    {
      id: "q1",
      question: "手術用手洗いの最低時間は？",
      options: ["3分", "5分", "6分", "10分"],
      correctAnswer: 2,
      explanation: "手術用手洗いは最低6分間行う必要があります。これは細菌数を効果的に減少させるために必要な時間です。"
    },
    {
      id: "q2",
      question: "器械出し時に最も重要なことは？",
      options: ["スピード", "無菌操作", "声の大きさ", "器械の知識"],
      correctAnswer: 1,
      explanation: "器械出しにおいて最も重要なのは無菌操作の維持です。感染リスクを防ぐために最優先で考える必要があります。"
    },
    {
      id: "q3",
      question: "「赤バイ」の正式名称は？",
      options: ["電気メス", "マイクロバイポーラ鉗子", "止血鉗子", "持針器"],
      correctAnswer: 1,
      explanation: "「赤バイ」は「マイクロバイポーラ鉗子」の通称です。赤いマーキングがあることからこの名前で呼ばれています。"
    }
  ];

  const userStats = {
    totalLessonsCompleted: 17,
    totalQuizzesPassed: 12,
    averageScore: 89,
    studyTimeThisWeek: 180, // 分
    rank: "中級者",
    achievements: [
      { name: "初回完了", icon: "🎯", description: "最初のレッスンを完了" },
      { name: "週間目標達成", icon: "📅", description: "週3時間の学習を達成" },
      { name: "クイズマスター", icon: "🧠", description: "10回連続でクイズに合格" }
    ],
    weakAreas: ["緊急時対応", "複雑な器械配置"],
    strongAreas: ["基本的な器械出し", "清潔操作"]
  };

  const startQuiz = () => {
    setQuizMode(true);
    setCurrentQuizQuestion(0);
    setUserAnswers([]);
  };

  const answerQuestion = (answerIndex) => {
    const newAnswers = [...userAnswers, answerIndex];
    setUserAnswers(newAnswers);
    
    if (currentQuizQuestion < quizQuestions.length - 1) {
      setCurrentQuizQuestion(currentQuizQuestion + 1);
    } else {
      // クイズ終了
      const score = newAnswers.reduce((acc, answer, index) => {
        return acc + (answer === quizQuestions[index].correctAnswer ? 1 : 0);
      }, 0);
      alert(`クイズ完了！ ${quizQuestions.length}問中${score}問正解（${Math.round(score / quizQuestions.length * 100)}%）`);
      setQuizMode(false);
    }
  };

  const renderOverview = () => (
    <div className="space-y-6">
      {/* ダッシュボード */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <BookOpen className="w-8 h-8 text-blue-600" />
              <div>
                <p className="text-sm text-gray-600">完了レッスン</p>
                <p className="text-2xl font-bold">{userStats.totalLessonsCompleted}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Target className="w-8 h-8 text-green-600" />
              <div>
                <p className="text-sm text-gray-600">平均スコア</p>
                <p className="text-2xl font-bold">{userStats.averageScore}%</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Clock className="w-8 h-8 text-purple-600" />
              <div>
                <p className="text-sm text-gray-600">今週の学習時間</p>
                <p className="text-2xl font-bold">{Math.floor(userStats.studyTimeThisWeek / 60)}h {userStats.studyTimeThisWeek % 60}m</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Star className="w-8 h-8 text-yellow-600" />
              <div>
                <p className="text-sm text-gray-600">現在のランク</p>
                <p className="text-lg font-bold">{userStats.rank}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* 学習モジュール */}
      <Card>
        <CardContent className="pt-6">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <GraduationCap className="w-5 h-5" />
            学習モジュール
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {learningModules.map(module => (
              <Card key={module.id} className="hover:shadow-md transition-shadow cursor-pointer">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`text-2xl`}>{module.icon}</div>
                    <div className="flex-1">
                      <h3 className="font-semibold">{module.title}</h3>
                      <Badge variant="outline" className="text-xs">
                        {module.level}
                      </Badge>
                    </div>
                  </div>
                  
                  <p className="text-sm text-gray-600 mb-3">{module.description}</p>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>進捗</span>
                      <span>{module.progress}%</span>
                    </div>
                    <Progress value={module.progress} className="h-2" />
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>{module.completedLessons}/{module.lessons} レッスン</span>
                      <span>{module.duration}</span>
                    </div>
                  </div>

                  <Button 
                    className="w-full mt-3"
                    variant={module.progress > 0 ? "default" : "outline"}
                    onClick={() => setActiveModule("lessons")}
                  >
                    {module.progress > 0 ? "続きから学習" : "学習開始"}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* アチーブメント */}
      <Card>
        <CardContent className="pt-6">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <Award className="w-5 h-5" />
            アチーブメント
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {userStats.achievements.map((achievement, index) => (
              <div key={index} className="flex items-center gap-3 p-3 border rounded">
                <div className="text-2xl">{achievement.icon}</div>
                <div>
                  <div className="font-medium">{achievement.name}</div>
                  <div className="text-sm text-gray-600">{achievement.description}</div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderLessons = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">基本的な器械出し - レッスン一覧</h2>
        <Button variant="outline" onClick={() => setActiveModule("overview")}>
          ← 概要に戻る
        </Button>
      </div>

      <div className="space-y-4">
        {detailedLessons.map((lesson, index) => (
          <Card key={lesson.id} className="hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-600 font-semibold">
                      {index + 1}
                    </div>
                    <h3 className="text-lg font-semibold">{lesson.title}</h3>
                    {lesson.completed ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <Clock className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                  
                  <p className="text-gray-600 mb-3">{lesson.description}</p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <h4 className="font-medium text-sm mb-2 flex items-center gap-1">
                        <Lightbulb className="w-4 h-4" />
                        重要ポイント
                      </h4>
                      <ul className="text-sm space-y-1">
                        {lesson.keyPoints.map((point, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <span className="text-blue-600 mt-1">•</span>
                            <span>{point}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <div>
                      <h4 className="font-medium text-sm mb-2 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        よくある間違い
                      </h4>
                      <ul className="text-sm space-y-1">
                        {lesson.commonMistakes.map((mistake, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <span className="text-red-600 mt-1">•</span>
                            <span>{mistake}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {lesson.duration}
                    </span>
                    <Badge variant="outline">{lesson.type}</Badge>
                    {lesson.completed && lesson.score && (
                      <Badge className="bg-green-100 text-green-800">
                        スコア: {lesson.score}%
                      </Badge>
                    )}
                  </div>
                </div>

                <div className="flex flex-col gap-2 ml-4">
                  <Button 
                    onClick={() => setCurrentLesson(lesson)}
                    disabled={!lesson.completed && index > 0 && !detailedLessons[index - 1].completed}
                  >
                    {lesson.completed ? "復習" : "学習開始"}
                  </Button>
                  {lesson.completed && (
                    <Button variant="outline" size="sm">
                      <RotateCcw className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardContent className="p-6 text-center">
          <h3 className="text-lg font-semibold mb-2">理解度チェック</h3>
          <p className="text-gray-600 mb-4">
            学習した内容の理解度をクイズで確認しましょう
          </p>
          <Button onClick={startQuiz} className="w-48">
            <HelpCircle className="w-4 h-4 mr-2" />
            クイズに挑戦
          </Button>
        </CardContent>
      </Card>
    </div>
  );

  const renderQuiz = () => {
    const currentQuestion = quizQuestions[currentQuizQuestion];
    
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2">理解度チェッククイズ</h2>
          <div className="flex justify-center gap-4 text-sm text-gray-600">
            <span>問題 {currentQuizQuestion + 1} / {quizQuestions.length}</span>
            <span>進捗: {Math.round((currentQuizQuestion / quizQuestions.length) * 100)}%</span>
          </div>
          <Progress value={(currentQuizQuestion / quizQuestions.length) * 100} className="mt-2 h-2" />
        </div>

        <Card>
          <CardContent className="pt-6">
            <h3 className="text-xl font-semibold text-center mb-6">
              問題 {currentQuizQuestion + 1}
            </h3>
            
            <div className="space-y-6">
              <h3 className="text-lg font-medium text-center">
                {currentQuestion.question}
              </h3>

              <div className="space-y-3">
                {currentQuestion.options.map((option, index) => (
                  <Button
                    key={index}
                    variant="outline"
                    className="w-full p-4 h-auto text-left justify-start"
                    onClick={() => answerQuestion(index)}
                  >
                    <span className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center mr-3 text-sm font-medium">
                      {String.fromCharCode(65 + index)}
                    </span>
                    {option}
                  </Button>
                ))}
              </div>

              <div className="text-center text-sm text-gray-600">
                選択肢をクリックして答えてください
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="text-center">
          <Button variant="outline" onClick={() => setQuizMode(false)}>
            クイズを終了
          </Button>
        </div>
      </div>
    );
  };

  const renderLessonView = () => {
    if (!currentLesson) return null;

    return (
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold">{currentLesson.title}</h2>
            <p className="text-gray-600">{currentLesson.description}</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setVoiceEnabled(!voiceEnabled)}
            >
              {voiceEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            </Button>
            <Button variant="outline" onClick={() => setCurrentLesson(null)}>
              レッスン一覧に戻る
            </Button>
          </div>
        </div>

        <Card>
          <CardContent className="p-6">
            {/* 学習コンテンツエリア */}
            <div className="aspect-video bg-gray-100 rounded-lg flex items-center justify-center mb-6">
              <div className="text-center">
                <Play className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">
                  {currentLesson.type === "video" && "動画コンテンツ"}
                  {currentLesson.type === "interactive" && "インタラクティブ学習"}
                  {currentLesson.type === "simulation" && "シミュレーション"}
                </p>
                <p className="text-sm text-gray-500">再生時間: {currentLesson.duration}</p>
              </div>
            </div>

            {/* 学習ポイント */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Lightbulb className="w-5 h-5 text-yellow-500" />
                  学習ポイント
                </h3>
                <ul className="space-y-2">
                  {currentLesson.keyPoints.map((point, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600 mt-1 flex-shrink-0" />
                      <span className="text-sm">{point}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-red-500" />
                  注意すべき点
                </h3>
                <ul className="space-y-2">
                  {currentLesson.commonMistakes.map((mistake, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <XCircle className="w-4 h-4 text-red-600 mt-1 flex-shrink-0" />
                      <span className="text-sm">{mistake}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="mt-6 flex justify-center gap-4">
              <Button>
                <Play className="w-4 h-4 mr-2" />
                学習を開始
              </Button>
              {currentLesson.completed && (
                <Button variant="outline">
                  <RotateCcw className="w-4 h-4 mr-2" />
                  もう一度学習
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  return (
    <div className="w-full max-w-7xl mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">教育・トレーニングシステム</h1>
        <p className="text-gray-600">手術室業務の段階的学習プログラム</p>
      </div>

      {quizMode ? renderQuiz() : 
       currentLesson ? renderLessonView() :
       activeModule === "lessons" ? renderLessons() : 
       renderOverview()}
    </div>
  );
}